/**
 * Utility functions for form state management and value comparison
 */

/**
 * Checks if a value is considered "empty" (null, undefined, or empty string)
 * Useful for treating optional form fields consistently
 */
export function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Deep comparison for arrays (order-independent for simple arrays)
 */
export function areArraysEqual(
  a: unknown[] | null | undefined,
  b: unknown[] | null | undefined
): boolean {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

/**
 * Compares two values for equality, handling various types
 * Treats null, undefined, and empty string as equivalent "empty" values
 *
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns true if values are considered equal
 */
export function areValuesEqual(a: unknown, b: unknown): boolean {
  // Direct equality check
  if (a === b) return true;

  // Handle null/undefined/empty string - all considered equivalent "empty" values
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  if (isEmptyValue(a) || isEmptyValue(b)) return false;

  // Handle arrays (including empty arrays)
  if (Array.isArray(a) && Array.isArray(b)) {
    return areArraysEqual(a, b);
  }

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle objects (simple comparison via JSON)
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  // If we reached here, values are different primitives
  return false;
}

/**
 * Normalizes values for consistent comparison.
 * Treats undefined, null, and empty string as equivalent for optional fields.
 * Rounds numbers to avoid floating point precision issues.
 */
function normalizeValue(value: any): any {
  // Handle Date objects
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Normalize all "empty" representations to null so different ways of expressing
  // "no value" compare equal: undefined (JSON.stringify omits it), empty string
  // (e.g. a cleared SingleSelect / text input sets ''), and empty array (a cleared
  // MultiSelect sets []). Without this, clearing a field that started as null leaves
  // the form dirty even though it's back to its initial empty state.
  // Note: this only collapses empties to each other — a non-empty initial value
  // (e.g. "abc") still differs from a cleared '', so real clears stay dirty.
  if (value === undefined || value === '') {
    return null;
  }

  // Round numbers to 2 decimal places to avoid floating point errors
  if (typeof value === 'number') {
    return Math.round(value * 100) / 100;
  }

  // Recursively normalize arrays; an empty array is an "empty" value → null.
  if (Array.isArray(value)) {
    return value.length === 0 ? null : value.map(normalizeValue);
  }

  // Recursively normalize objects
  if (value !== null && typeof value === 'object') {
    const normalized: any = {};
    for (const key in value) {
      normalized[key] = normalizeValue(value[key]);
    }
    return normalized;
  }

  return value;
}

/**
 * Compares current form values with initial values to determine if form is actually dirty.
 * This is useful when react-hook-form's isDirty doesn't properly reset when values
 * are manually reverted to their original state.
 *
 * Handles edge cases:
 * - undefined vs null (treated as equivalent)
 * - Date objects (compared by ISO string)
 * - Nested objects and arrays
 *
 * @param currentValues - Current form values from useWatch
 * @param initialValues - Initial form values
 * @returns true if values differ from initial state, false otherwise
 */
export function compareFormValues(currentValues: any, initialValues: any): boolean {
  if (!currentValues) return false;

  try {
    // Normalize both values to handle undefined/null differences
    const normalizedCurrent = normalizeValue(currentValues);
    const normalizedInitial = normalizeValue(initialValues);

    // Compare stringified versions
    const currentStr = JSON.stringify(normalizedCurrent);
    const initialStr = JSON.stringify(normalizedInitial);

    return currentStr !== initialStr;
  } catch (error) {
    return false;
  }
}
